<template>
  <div class="fill-calculator">
    <div class="section-title">Calculate a fill</div>

    <div class="inputs">
      <label>
        <span class="input-label">O2 %</span>
        <el-input-number
          v-model="o2"
          :min="21"
          :max="100"
          :precision="0"
          :step="1"
          controls-position="right"
          size="small" />
      </label>
      <label>
        <span class="input-label">He %</span>
        <el-input-number
          v-model="he"
          :min="0"
          :max="100"
          :precision="0"
          :step="1"
          controls-position="right"
          size="small" />
      </label>
      <label>
        <span class="input-label">Cylinder (L)</span>
        <el-input-number
          v-model="cylinderSize"
          :min="0.1"
          :precision="1"
          :step="1"
          controls-position="right"
          size="small" />
      </label>
      <label>
        <span class="input-label">Fill pressure (bar)</span>
        <el-input-number
          v-model="fillPressure"
          :min="1"
          :precision="0"
          :step="10"
          controls-position="right"
          size="small" />
      </label>
    </div>

    <div v-if="validationMessage" class="validation">{{ validationMessage }}</div>

    <div v-else-if="litres" class="result">
      <div class="breakdown">
        <span v-if="litres.air > 0">{{ formatLitres(litres.air) }} L air</span>
        <span v-if="litres.o2 > 0">+ {{ formatLitres(litres.o2) }} L O2</span>
        <span v-if="litres.he > 0">+ {{ formatLitres(litres.he) }} L He</span>
      </div>
      <div v-if="missingComponent" class="missing">
        {{ missingComponent }} price not configured for this station
      </div>
      <div v-else class="total">
        Total: {{ currency }} {{ formatPrice(total) }}
      </div>
    </div>
  </div>
</template>

<script>
import { blendLitres } from './blendLitres'

export default {
  name: 'FillCalculator',
  props: {
    components: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      o2: 21,
      he: 0,
      cylinderSize: 12,
      fillPressure: 230
    }
  },
  computed: {
    validationMessage () {
      if (!Number.isFinite(this.o2) || !Number.isFinite(this.he)) {
        return 'Enter O2 and He percentages.'
      }
      if (!Number.isFinite(this.cylinderSize) || this.cylinderSize <= 0) {
        return 'Enter a cylinder size greater than 0.'
      }
      if (!Number.isFinite(this.fillPressure) || this.fillPressure <= 0) {
        return 'Enter a fill pressure greater than 0.'
      }
      if (this.o2 < 21) {
        return 'O2 must be at least 21% (hypoxic mixes are not supported).'
      }
      if (this.o2 > 100 || this.he > 100) {
        return 'O2 and He must each be 100% or less.'
      }
      if (this.o2 + this.he > 100) {
        return 'O2 + He cannot exceed 100%.'
      }
      return null
    },
    litres () {
      if (this.validationMessage) return null
      return blendLitres({
        o2Pct: this.o2,
        hePct: this.he,
        cylinderSize: this.cylinderSize,
        fillPressure: this.fillPressure
      })
    },
    missingComponent () {
      if (!this.litres) return null
      if (this.litres.air > 0 && this.components.air === null) return 'Air'
      if (this.litres.o2 > 0 && this.components.o2 === null) return 'O2'
      if (this.litres.he > 0 && this.components.he === null) return 'Helium'
      return null
    },
    total () {
      if (!this.litres || this.missingComponent) return null
      return (
        this.litres.air * (this.components.air || 0) +
        this.litres.o2 * (this.components.o2 || 0) +
        this.litres.he * (this.components.he || 0)
      )
    }
  },
  methods: {
    formatLitres (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(0)
    },
    formatPrice (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(2)
    }
  }
}
</script>

<style lang="scss" scoped>
  .section-title {
    font-size: 12px;
    text-transform: uppercase;
    color: $text-light;
    margin-bottom: 10px;
  }

  .inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 15px;

    label {
      display: flex;
      flex-direction: column;

      .input-label {
        font-size: 11px;
        color: $text-light;
        margin-bottom: 4px;
      }
    }
  }

  .validation {
    color: #e6a23c;
    font-size: 12px;
    padding: 8px 0;
  }

  .result {
    .breakdown {
      font-size: 12px;
      color: $text-light;
      margin-bottom: 6px;

      span + span {
        margin-left: 4px;
      }
    }

    .total {
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    .missing {
      font-size: 12px;
      color: #e6a23c;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/stations/station/Pricing/FillCalculator.vue